/** @jsxImportSource @emotion/react */

import React, { useMemo } from 'react';
import styled from 'styled-components';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import Placeholder from '@component/common/placeholder';
import * as color from '@constant/color';
import { v4 as uuid4 } from 'uuid';
import { parseSentenceWithChoice } from '@util/workbook';
import { shuffleArrayWithSeed } from '@util/random';
import { GRAMMAR_CHIP_HEIGHT_JP, GRAMMAR_CHIP_HEIGHT_EN, GRAMMAR_CHIP_PADDING } from '@constant/design';

interface IProps {
  workbookKey: string;
  choice: string[];
  choicePronunciation: string[];
  answer: string[];
  sentence: string;
  isAnswerView: boolean;
  selectedWords: { word: string; idx: number }[];
  handleWordSelect: (word: string, idx: number) => void;
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: ${size.MARGIN_1};
`;

const WordChip = styled.div<{ empty: boolean; $isJapanese: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${(props) => (props.$isJapanese ? GRAMMAR_CHIP_HEIGHT_JP : GRAMMAR_CHIP_HEIGHT_EN)};
  padding: ${GRAMMAR_CHIP_PADDING};
  border: ${size.BORDER_WIDTH_1} solid ${color.GRAY_3};
  border-radius: ${size.BORDER_RADIUS_1};
  box-shadow: ${(props) => (props.empty ? 'none' : color.SURFACE_SHADOW)};
  background-color: ${(props) => (props.empty ? color.GRAY_3 : color.WHITE_1)};
  cursor: ${(props) => (props.empty ? 'default' : 'pointer')};

  &:hover {
    ${(props) =>
      !props.empty &&
      `
        box-shadow: ${color.SURFACE_SHADOW};
        transform: translateY(-1px);
      `}
  }
`;

const GrammarChoice: React.FC<IProps> = ({
  workbookKey,
  choice,
  choicePronunciation,
  answer,
  sentence,
  isAnswerView,
  selectedWords,
  handleWordSelect,
}) => {
  // ############### function ###############
  // answer를 기반으로 부족한 단어를 choice에 추가
  const parsedChoice = useMemo(() => parseSentenceWithChoice(sentence, choice), [choice, sentence]);

  const shuffledChoice = useMemo(() => shuffleArrayWithSeed(parsedChoice, workbookKey), [parsedChoice, workbookKey]);

  // ############### handler ###############
  const handleWordClick = (word: string, idx: number) => {
    if (isAnswerView) return;
    handleWordSelect(word, idx);
  };

  return (
    <Wrapper>
      {shuffledChoice.map((word, index) => {
        const isSelected = selectedWords.some((selected) => selected.idx === index);
        const pronunciation = choicePronunciation?.[choice.indexOf(word)];
        return (
          <WordChip
            key={uuid4()}
            empty={isSelected}
            $isJapanese={choicePronunciation.length > 0}
            onClick={() => !isSelected && handleWordClick(word, index)}>
            <Placeholder visible={!isSelected} placeholder={null}>
              <TextBlock
                text={word}
                annotation={pronunciation ? { reading: pronunciation } : undefined}
                reserveAnnotationSpace={choicePronunciation.length > 0}
                design={{
                  text: {
                    size: 'medium',
                    color: color.BLACK_2,
                    bold: false,
                  },
                  box: { alignX: 'center' },
                }}
              />
            </Placeholder>
          </WordChip>
        );
      })}
    </Wrapper>
  );
};

export default GrammarChoice;
